/**
 * useQuery: a tiny fetch hook. Cached by key: a revisit shows the last data at once, and refreshes behind it
 * only once that data is older than `FRESH_MS`, so clicking around never asks again for what is on hand.
 * Screens asking for the same key at once share one request, and every screen showing a key gets what any of them fetched. Loading, error, data, refetch and an optional poll.
 * With `keep`, a new key that is not on hand keeps the last key's data on screen (`stale`) until its own lands:
 * a tab, a page, a sort or a filter never blanks the list into a skeleton.
 * A fetcher that loads in parts may hand each part to `progress` as it lands: the screen shows it at once (`partial`), and the rest follows behind.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** How long fetched data is taken as current: a remount inside it shows the data and asks nothing. A poll, `refetch` and `invalidate` always ask. */
export const FRESH_MS = 60_000;

/** Keys kept at most: past it the least recently used that no screen shows are let go (a day of clicking through people and filters). */
export const MAX_KEPT = 150;

/** Least recently used first: a read or a write moves a key to the end. */
const cache = new Map<string, { data: unknown; at: number }>();
/** Requests in flight, by key: a second asker waits for the first instead of sending its own. */
const inflight = new Map<string, Promise<unknown>>();
/** The queries on screen, by key: `invalidate` asks each to fetch again. */
const mounted = new Map<string, Set<() => Promise<void>>>();
/** The queries on screen, by key: whichever of them fetched, each is handed what landed (one poll keeps them all current), each part on the way (`partial`), and a failure. */
const listeners = new Map<string, Set<(heard: { data: unknown; partial: boolean } | { error: Error }) => void>>();
/** The last part handed on by a request still in flight, by key: a screen that mounts mid-load shows it at once. */
const parts = new Map<string, unknown>();

/** What a fetcher is given: `progress(part)` hands on what has landed so far, before the whole answer. */
export type Fetcher<T> = (progress: (part: T) => void) => Promise<T>;

export interface QueryResult<T> {
  data: T | undefined;
  error: Error | null;
  /** True only before the first data or error: show the skeleton. */
  loading: boolean;
  /** True while any request is in flight, including a background refresh: keep the content. */
  fetching: boolean;
  /** `data` is the previous key's, kept on screen (`keep`) while this key's loads: dim it, and act on nothing in it. */
  stale: boolean;
  /** `data` is the first part of this key's answer, handed on by the fetcher's `progress`: show it, but count, sort or export nothing from it until the whole lands. */
  partial: boolean;
  refetch: () => Promise<void>;
}

interface State<T> {
  key: string | null;
  data: T | undefined;
  error: Error | null;
  fetching: boolean;
  /** `data` belongs to an earlier key. */
  stale: boolean;
  /** `data` is a part of this key's answer. */
  partial: boolean;
}

function recall(key: string): { data: unknown; at: number } | undefined {
  const hit = cache.get(key);
  if (hit) {
    cache.delete(key);
    cache.set(key, hit);
  }
  return hit;
}

function keep(key: string, data: unknown): void {
  cache.delete(key);
  cache.set(key, { data, at: Date.now() });
  for (const old of cache.keys()) {
    if (cache.size <= MAX_KEPT) break;
    if (!mounted.has(old)) cache.delete(old);
  }
}

/** The state for `key`: its data when on hand, else the part of it on hand, else `kept` (the last key's, with `keep`) marked stale. */
function initial<T>(key: string | null, kept?: T): State<T> {
  const hit = key ? recall(key) : undefined;
  if (hit) return { key, data: hit.data as T, error: null, fetching: !fresh(hit), stale: false, partial: false };
  if (key && parts.has(key)) return { key, data: parts.get(key) as T, error: null, fetching: true, stale: false, partial: true };
  const keeping = !!key && kept !== undefined;
  return { key, data: keeping ? kept : undefined, error: null, fetching: !!key, stale: keeping, partial: false };
}

const fresh = (hit: { at: number } | undefined) => !!hit && Date.now() - hit.at < FRESH_MS;

/** The one request for a key: joins the one in flight, else starts it. */
function request<T>(key: string, fetcher: Fetcher<T>): Promise<T> {
  let pending = inflight.get(key) as Promise<T> | undefined;
  if (!pending) {
    // A request `invalidate` let go of is not the truth any more: its parts and its answer go only to who asked.
    const current = () => inflight.get(key) === pending;
    const progress = (part: T) => {
      if (!current()) return;
      parts.set(key, part);
      for (const hear of listeners.get(key) ?? []) hear({ data: part, partial: true });
    };
    pending = fetcher(progress).then(
      (data) => {
        if (current()) {
          keep(key, data);
          for (const hear of listeners.get(key) ?? []) hear({ data, partial: false });
        }
        return data;
      },
      (err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        if (current()) for (const hear of listeners.get(key) ?? []) hear({ error });
        throw error;
      },
    );
    const settled = () => {
      if (current()) {
        inflight.delete(key);
        parts.delete(key);
      }
    };
    pending.then(settled, settled);
    inflight.set(key, pending);
  }
  return pending;
}

/**
 * `key` names the request (include every parameter); null skips it. `fetcher` may change between
 * renders; only `key` decides when to fetch.
 */
export function useQuery<T>(
  key: string | null,
  fetcher: Fetcher<T>,
  options: { intervalMs?: number; keep?: boolean } = {},
): QueryResult<T> {
  const [held, setState] = useState<State<T>>(() => initial<T>(key));
  // A new key answers from its own state in this very render, not the last key's.
  let state = held;
  if (held.key !== key) {
    state = initial<T>(key, options.keep && !held.error ? held.data : undefined);
    setState(state);
  }

  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  });
  const seq = useRef(0);

  const run = useCallback(
    async (force: boolean) => {
      if (!key) return;
      // Every run is the latest: an earlier key's answer, landing after, is not shown under this one.
      const id = ++seq.current;
      // Data fetched a moment ago is current: nothing to ask.
      const hit = cache.get(key);
      if (!force && fresh(hit)) {
        // It may have landed between this screen's render and its listening: show it.
        setState((s) => (s.key === key && s.data !== hit?.data ? { key, data: hit?.data as T, error: null, fetching: false, stale: false, partial: false } : s));
        return;
      }
      setState((s) => (s.fetching ? s : { ...s, fetching: true }));
      try {
        const data = await request(key, fetcherRef.current);
        if (id !== seq.current) return;
        setState({ key, data, error: null, fetching: false, stale: false, partial: false });
      } catch (err) {
        if (id !== seq.current) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState((s) => ({ ...s, error, fetching: false, stale: false, partial: false }));
      }
    },
    [key],
  );
  const refetch = useCallback(() => run(true), [run]);

  // Listening comes first: a read that hands on a part as it starts reaches this screen.
  useEffect(() => {
    if (!key) return;
    // A part never takes the place of a whole answer on screen (a refresh's first part): it fills a skeleton, or the last key's rows.
    // A failure reaches a screen showing a part (another screen's read, a warmed one): it would otherwise wait on it for good.
    const hear = (heard: { data: unknown; partial: boolean } | { error: Error }) =>
      setState((s) => {
        if (s.key !== key) return s;
        if ("error" in heard) return s.partial ? { ...s, error: heard.error, fetching: false, partial: false } : s;
        if (heard.partial && s.data !== undefined && !s.partial && !s.stale && !s.error) return s;
        return { ...s, data: heard.data as T, error: null, stale: false, partial: heard.partial };
      });
    const set = listeners.get(key) ?? new Set();
    listeners.set(key, set.add(hear));
    return () => {
      set.delete(hear);
      if (!set.size) listeners.delete(key);
    };
  }, [key]);

  useEffect(() => {
    void run(false);
    if (!key) return;
    const set = mounted.get(key) ?? new Set();
    mounted.set(key, set.add(refetch));
    return () => {
      set.delete(refetch);
      if (!set.size) mounted.delete(key);
    };
  }, [run, refetch, key]);

  const { intervalMs } = options;
  useEffect(() => {
    if (!intervalMs || !key) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refetch();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [refetch, intervalMs, key]);

  // A failed fetch hides the last data: the screen shows its error line, not a stale table.
  const data = state.error ? undefined : state.data;
  return {
    data,
    error: state.error,
    loading: !!key && data === undefined && state.error === null,
    fetching: state.fetching,
    stale: !state.error && state.stale,
    partial: !state.error && state.partial,
    refetch,
  };
}

/** Start fetching `key` now unless it is fresh or on its way, so the screen that asks for it next shows it at once. */
export function prefetch<T>(key: string, fetcher: Fetcher<T>): void {
  if (fresh(cache.get(key)) || inflight.has(key)) return;
  request(key, fetcher).catch(() => undefined);
}

/** After a write: forget cached data (all of it, or keys starting with `prefix`) and fetch again what is on screen under those keys. The content stays up while it does; the promise settles when it has landed. */
export function invalidate(prefix = ""): Promise<void> {
  for (const key of cache.keys()) if (key.startsWith(prefix)) cache.delete(key);
  // A request sent before the write may carry what the write changed: it is not joined.
  for (const key of inflight.keys())
    if (key.startsWith(prefix)) {
      inflight.delete(key);
      parts.delete(key);
    }
  const asked: Promise<void>[] = [];
  for (const [key, set] of mounted) if (key.startsWith(prefix)) for (const refetch of set) asked.push(refetch());
  return Promise.all(asked).then(() => undefined);
}
